// Utils
import {
  LinkCreationService,
  formatGameScore,
  formatGameStatus,
  getMatchDate,
  getNameByStatus,
  getStatusDate,
  getWinnerReason,
} from '@sportal365/smp-widget-utils';

// Constants
import { TARGET_BLANK, TARGET_SELF } from '../../../../../packages/constants/windowTarget';
import { FINISHED, LIVE, NOT_STARTED } from '../../../../../packages/constants/statusTypes';
import { assetsPlaceholder } from '../../../../../packages/constants/assetsPlaceholder';
import { COMPETITION_ENTITY, STANDINGS_ENTITY } from '../../../../../packages/constants/entityTypes';

// Types
import { IConfigEntityLinks, IMatchV2, SDKDateOptions, ShortNameType } from '../../../../../packages/types';

interface INormalizeTournamentProgrammeParams {
  matches?: IMatchV2[];
  dataTeamNameType?: ShortNameType | null;
  dataEntityLinks?: IConfigEntityLinks | null;
  sdkOptions: SDKDateOptions;
}

export const normalizeTournamentProgrammeResponseData = ({
  matches,
  dataTeamNameType,
  dataEntityLinks,
  sdkOptions,
}: INormalizeTournamentProgrammeParams) => {
  const linkService = dataEntityLinks && new LinkCreationService(dataEntityLinks);
  const target = dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF;

  if (!matches) return [];

  const normalizeEvent = ({ id, slug, kickoff_time, status, minute, home_team, away_team, score, season, odds, winner }: IMatchV2) => ({
    id,
    date: kickoff_time && status?.type ? getStatusDate(kickoff_time, status?.type, sdkOptions) : '',
    status: formatGameStatus(status, minute),
    startTime: status?.type === NOT_STARTED && kickoff_time ? getMatchDate(kickoff_time, sdkOptions, 'HH:mm') : '',
    isLive: status?.type === LIVE ? true : false,
    isFinished: status?.type === FINISHED ? true : false,
    minute: minute,
    score: formatGameScore(score?.display?.home, score?.display?.away, status).score,
    url: linkService?.createMatchUrl(id, home_team, away_team, slug) ?? '#',
    target: target,
    tournament: season?.tournament?.name ?? '',
    homeTeam: {
      id: home_team.id,
      name: getNameByStatus({ ...home_team, status: dataTeamNameType }),
      logo: home_team.assets?.logo?.url ?? assetsPlaceholder.team,
      score: formatGameScore(score?.display?.home, score?.display?.away, status).homeScoreResult,
      isWinner: winner?.match?.id === home_team.id ? true : false,
      winnerReason: getWinnerReason(home_team.id, winner),
    },
    awayTeam: {
      id: away_team.id,
      name: getNameByStatus({ ...away_team, status: dataTeamNameType }),
      logo: away_team.assets?.logo?.url ?? assetsPlaceholder.team,
      score: formatGameScore(score?.display?.home, score?.display?.away, status).awayScoreResult,
      isWinner: winner?.match?.id === away_team.id ? true : false,
      winnerReason: getWinnerReason(away_team.id, winner),
    },
    odds: odds && odds.length ? odds : [],
  });

  const groupEventsByStage = matches.reduce((acc: { [stageId: string]: IMatchV2[] }, event) => {
    const stageId = event.stage?.id;
    if (!acc[stageId]) {
      acc[stageId] = [];
    }
    acc[stageId].push(event);
    return acc;
  }, {});

  const groupStageEventsByRound = Object.entries(groupEventsByStage).map(([_, stageEvents]) => {
    const stage = {
      id: stageEvents[0]?.stage?.id ?? '',
      name: stageEvents[0]?.stage?.name ?? '',
      slug: stageEvents[0]?.stage.slug ?? '',
      url:
        linkService?.createEntityUrl(
          stageEvents[0].season?.tournament?.id ?? '',
          COMPETITION_ENTITY,
          '',
          stageEvents[0].season?.tournament?.slug ?? '',
        ) ?? '#',
      target: target,
    };

    const groupedByRound = stageEvents.reduce((acc: { [roundId: string]: IMatchV2[] }, event) => {
      const roundId = event.round.key;
      if (!acc[roundId]) {
        acc[roundId] = [];
      }
      acc[roundId].push(event);
      return acc;
    }, {});

    const roundsData = Object.entries(groupedByRound).map(([_, roundEvents]) => ({
      round: { id: roundEvents[0]?.round?.key ?? '', name: roundEvents[0]?.round?.name ?? '' },
      matches: roundEvents.map((event) => normalizeEvent(event)),
    }));

    return {
      stage: {
        id: stage.id,
        name: stage.name,
        url: stage.url,
        target: stage.target,
        standingsUrl: linkService?.createEntityUrl(stage.id, STANDINGS_ENTITY, '', stage.slug) ?? '#',
      },
      data: roundsData,
    };
  });

  return groupStageEventsByRound;
};
